// src/components/DiscipleSettings.js

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { doc, getDoc, updateDoc, setDoc, deleteDoc, serverTimestamp, collection, addDoc, getDocs, query, where, increment } from 'firebase/firestore';
import { db } from '../firebase';
import { Switch } from '@headlessui/react';
import { format } from 'date-fns';
import { formatInTimeZone, toZonedTime, fromZonedTime } from 'date-fns-tz';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import DiscipleImageUpload from './DiscipleImageUpload';
import { UserCircleIcon, ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';
import { uploadDiscipleImage } from '../utils/imageUpload';
import ImageUpload from './ImageUpload';
import { CloudArrowUpIcon } from '@heroicons/react/24/outline';

const getImageUrl = (url) => {
  if (!url) return null;
  // Remove any emulator URLs and return the clean storage URL
  return url.replace('http://localhost:9198/v0/b/', 'https://firebasestorage.googleapis.com/v0/b/');
};

const DiscipleSettings = ({ 
  isOpen,
  discipleId, 
  currentUser,
  initialData,
  onUpdate,
  onClose
}) => {
  // Add logging for initial props
  console.log('DiscipleSettings initialized with:', {
    discipleId,
    currentUser,
    hasInitialData: !!initialData,
    appID: currentUser?.appID
  });

  // Initialize discipleData state
  const [discipleData, setDiscipleData] = useState({
    name: initialData?.name || '',
    email: initialData?.email || '',
    phone: initialData?.phone || '',
    photoURL: initialData?.photoURL || '',
    birthday: initialData?.birthday || '',
    reminderSettings: {
      enabled: true,
      frequency: 7,
      preferredTime: '09:00',
      ...initialData?.reminderSettings
    }
  });
  const [isChild, setIsChild] = useState(initialData?.isChild === true);
  const [hasDevice, setHasDevice] = useState(initialData?.hasDevice === true);

  // Initialize reminderSettings with complete default structure
  const [reminderSettings, setReminderSettings] = useState({
    enabled: false,
    frequency: 7,
    preferredTime: "09:00",
    notificationMethod: 'fcm',
    lastMessageDate: null,
    nextReminderDate: null,
    quietHours: {
      start: "22:00",
      end: "08:00"
    }
  });

  // Meet in-person reminders state
  const [meetInPersonReminders, setMeetInPersonReminders] = useState(false);
  const [inPersonReminderRhythm, setInPersonReminderRhythm] = useState('monthly'); // 'weekly' | 'bi-weekly' | 'monthly'

  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);

  // Add state for confirmation dialog
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [pendingChange, setPendingChange] = useState(null);
  const [pendingChanges, setPendingChanges] = useState({});

  // Add time zone state and utilities
  const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  // Next reminder date display state
  const [nextReminderDateWithTime, setNextReminderDateWithTime] = useState(null);
  const [nextReminderTimezoneLabel, setNextReminderTimezoneLabel] = useState('');
  
  // Next notification date and time state
  const [nextNotificationDateAndTime, setNextNotificationDateAndTime] = useState(null);
  const [nextNotificationDateWithTime, setNextNotificationDateWithTime] = useState(null);
  const [showNextNotificationDatePicker, setShowNextNotificationDatePicker] = useState(false);
  const [nextNotificationDateInput, setNextNotificationDateInput] = useState('');
  const [nextNotificationTimeInput, setNextNotificationTimeInput] = useState('');

  // Helper function to get timezone in layman's terms (Eastern, Central, etc.)
  const getTimezoneLaymanTerm = (timezone) => {
    if (!timezone) return '';
    if (timezone.includes('New_York') || timezone.includes('Detroit') || timezone.includes('Indianapolis') || timezone.includes('Atlanta')) {
      return 'Eastern';
    }
    if (timezone.includes('Chicago') || timezone.includes('Dallas') || timezone.includes('Houston')) {
      return 'Central';
    }
    if (timezone.includes('Denver') || timezone.includes('Phoenix')) {
      return 'Mountain';
    }
    if (timezone.includes('Los_Angeles') || timezone.includes('Seattle') || timezone.includes('San_Francisco')) {
      return 'Pacific';
    }
    if (timezone.includes('Anchorage')) {
      return 'Alaska';
    }
    if (timezone.includes('Honolulu')) {
      return 'Hawaii';
    }
    // For other timezones, use a shortened version
    const parts = timezone.split('/');
    return parts[parts.length - 1]?.replace(/_/g, ' ') || timezone;
  };

  // Update the display of next reminder date with time and timezone
  const updateNextReminderDisplay = (dateValue, userTimezone) => {
    if (!dateValue) {
      setNextReminderDateWithTime(null);
      setNextReminderTimezoneLabel('');
      return;
    }

    try {
      // Parse the date value (could be Date object, ISO string, or Firestore Timestamp)
      let dateObj;
      if (dateValue instanceof Date) {
        dateObj = dateValue;
      } else if (dateValue.toDate && typeof dateValue.toDate === 'function') {
        // Firestore Timestamp
        dateObj = dateValue.toDate();
      } else if (typeof dateValue === 'string') {
        dateObj = new Date(dateValue);
      } else {
        dateObj = new Date(dateValue);
      }

      if (isNaN(dateObj.getTime())) {
        setNextReminderDateWithTime(null);
        setNextReminderTimezoneLabel('');
        return;
      }

      // Get user's timezone (from user data or device)
      const tz = userTimezone || userTimeZone;
      const timezoneLabel = getTimezoneLaymanTerm(tz);
      
      // Convert to user's timezone for display
      const dateInTz = toZonedTime(dateObj, tz);
      
      // Format date and time (matching mobile: MM/dd/yyyy at h:mm a)
      const dateStr = format(dateInTz, 'MM/dd/yyyy');
      const timeStr = format(dateInTz, 'h:mm a');
      
      setNextReminderDateWithTime(`${dateStr} at ${timeStr}`);
      setNextReminderTimezoneLabel(timezoneLabel);
    } catch (error) {
      console.error('Error formatting next reminder date:', error);
      setNextReminderDateWithTime(null);
      setNextReminderTimezoneLabel('');
    }
  };

  const formatLocalTime = (time) => {
    if (!time) return '';
    const [hours, minutes] = time.split(':');
    const date = new Date();
    date.setHours(parseInt(hours, 10), parseInt(minutes, 10), 0, 0);
    return formatInTimeZone(date, userTimeZone, 'HH:mm');
  };

  // Helper function to calculate next reminder date with timezone awareness (matching mobile app)
  const calculateNextReminderDateWithTimezone = (baseDate, preferredTimeStr, userTimezone, addDaysCount = 0) => {
    const [hour, minute] = preferredTimeStr.split(":").map(Number);
    
    // Convert base date to user's timezone to get the correct date components
    const dateInTz = toZonedTime(baseDate, userTimezone);
    
    // Add days if needed
    if (addDaysCount > 0) {
      dateInTz.setDate(dateInTz.getDate() + addDaysCount);
    }
    
    // Create a date object with the desired time components in the user's timezone
    const year = dateInTz.getFullYear();
    const month = dateInTz.getMonth();
    const day = dateInTz.getDate();
    const localDate = new Date(year, month, day, hour, minute, 0);
    
    // Convert from the user's timezone to UTC (matching backend logic)
    return fromZonedTime(localDate, userTimezone);
  };

  const calculateNextReminder = (settings, now = new Date()) => {
    const {
      createdAt,
      lastMessageDate,
      reminderSettings: {
        frequency,
        preferredTime,
        enabled
      }
    } = settings;

    if (!enabled) return null;

    // Parse preferred time
    const [prefHours, prefMinutes] = preferredTime.split(':').map(Number);
    
    let baseDate;
    
    // Determine base date for calculation
    if (lastMessageDate) {
      // Case 2: Use last message as reference
      baseDate = new Date(lastMessageDate);
    } else if (createdAt) {
      // Case 1: New disciple, use creation date
      baseDate = new Date(createdAt);
    } else {
      // Case 3: Re-enabling reminders, use current date
      baseDate = now;
    }

    // Set the preferred time
    baseDate.setHours(prefHours, prefMinutes, 0, 0);
    
    // If base date is in the past, add frequency days until we get a future date
    while (baseDate <= now) {
      baseDate.setDate(baseDate.getDate() + frequency);
    }
    
    return baseDate;
  };

  // Validation functions
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePhone = (phone) => {
    const phoneRegex = /^\+?[1-9]\d{1,14}$/;
    return phoneRegex.test(phone);
  };

  const validatePhotoUrl = (url) => {
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  };

  const validateTime = (time) => {
    const timeRegex = /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/;
    return timeRegex.test(time);
  };

  // Add this function to handle the initialization of mentor details
  const initializeMentorDetails = async () => {
    try {
      const mentorRef = doc(db, 'disciples', discipleId, 'mentorDetails', currentUser.uid);
      const mentorDoc = await getDoc(mentorRef);

      // Calculate nextInPersonReminderDate: one month from today (default monthly rhythm)
      const nextInPersonReminderDate = new Date();
      nextInPersonReminderDate.setMonth(nextInPersonReminderDate.getMonth() + 1);

      if (!mentorDoc.exists()) {
        // Create the initial mentor document with reminderSettings fields
        await setDoc(mentorRef, {
          uid: currentUser.uid,
          addedAt: new Date(),
          reminderSettings: {
            enabled: true,
            frequency: 7,
            preferredTime: "09:00",
            notificationMethod: 'fcm',
            lastMessageDate: null,
            nextReminderDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000),
            quietHours: {
              start: "22:00",
              end: "08:00"
            }
          },
          meetInPersonRemindersEnabled: false,
          inPersonReminderRhythm: 'monthly',
          nextInPersonReminderDate: nextInPersonReminderDate
        });
      } else if (!mentorDoc.data().reminderSettings) {
        // If mentor document exists but doesn't have reminderSettings, add them
        const existingData = mentorDoc.data();
        await updateDoc(mentorRef, {
          reminderSettings: {
            enabled: true,
            frequency: 7,
            preferredTime: "09:00",
            notificationMethod: 'fcm',
            lastMessageDate: null,
            nextReminderDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000),
            quietHours: {
              start: "22:00",
              end: "08:00"
            }
          },
          // Only add meet-in-person fields if they don't exist
          ...(existingData.meetInPersonRemindersEnabled === undefined && {
            meetInPersonRemindersEnabled: false,
            inPersonReminderRhythm: 'monthly',
            nextInPersonReminderDate: nextInPersonReminderDate
          })
        });
      }
    } catch (error) {
      console.error('Error initializing mentor details:', error);
      setError('Failed to initialize settings');
    }
  };

  const updateMentorDetailsStructure = async () => {
    if (!discipleId || !currentUser?.uid) return;
    
    try {
      const mentorRef = doc(db, 'disciples', discipleId, 'mentorDetails', currentUser.uid);
      const mentorDoc = await getDoc(mentorRef);
      
      if (mentorDoc.exists()) {
        const data = mentorDoc.data();
        
        // Prepare the updated structure
        const updatedData = {
          ...data,
          appID: data.uid,
          name: data.name || '',
          contactCardSent: data.contactCardSent || false,
          lastInteractionAt: data.lastInteractionAt || null,
          photoURL: data.photoURL || '',
          archived: data.archived || false,
          reminderSettings: {
            ...data.reminderSettings,
            enabled: data.reminderSettings?.enabled ?? true,
            frequency: data.reminderSettings?.frequency ?? 7,
            preferredTime: data.reminderSettings?.preferredTime ?? "09:00",
            notificationMethod: data.reminderSettings?.notificationMethod ?? 'fcm',
            lastMessageDate: data.reminderSettings?.lastMessageDate ?? null,
            nextReminderDate: data.reminderSettings?.nextReminderDate ?? null,
            quietHours: {
              ...data.reminderSettings?.quietHours,
              start: data.reminderSettings?.quietHours?.start ?? "22:00",
              end: data.reminderSettings?.quietHours?.end ?? "08:00"
            }
          }
        };

        // Update the document with the correct structure
        await updateDoc(mentorRef, updatedData);
        
        // Update local state
        setReminderSettings(updatedData.reminderSettings);
      }
    } catch (error) {
      console.error('Error updating mentor details structure:', error);
      setError('Failed to update settings structure');
    }
  };

  // Add this function near the top of your component
  const ensureCreatedAtField = async (mentorRef, currentData) => {
    if (!currentData.createdAt) {
      // For existing records without createdAt, use either:
      // 1. The earliest of lastMessageDate or nextReminderDate
      // 2. Or fallback to current timestamp
      let createdAt;
      
      if (currentData.lastMessageDate) {
        createdAt = currentData.lastMessageDate;
      } else if (currentData.reminderSettings?.nextReminderDate) {
        createdAt = currentData.reminderSettings.nextReminderDate;
      } else {
        createdAt = new Date().toISOString();
      }

      try {
        await updateDoc(mentorRef, {
          createdAt: createdAt
        });
        console.log('Added createdAt field:', createdAt);
        return {
          ...currentData,
          createdAt
        };
      } catch (error) {
        console.error('Error adding createdAt field:', error);
        return currentData;
      }
    }
    return currentData;
  };

  // Load disciple and mentor details on component mount
  useEffect(() => {
    const loadData = async () => {
      if (!discipleId || !currentUser?.uid) return;
      
      try {
        // First load disciple data
        const discipleRef = doc(db, 'disciples', discipleId);
        const discipleDoc = await getDoc(discipleRef);
        
        if (discipleDoc.exists()) {
          const discipleData = discipleDoc.data();
          setDiscipleData(prevData => ({
            ...prevData,
            photoURL: discipleData.photoURL || '',
            name: discipleData.name || '',
            email: discipleData.email || '',
            phone: discipleData.phone || '',
            birthday: discipleData.birthday || ''
          }));
          setIsChild(discipleData.isChild === true);
          setHasDevice(discipleData.hasDevice === true);
        }

        // Then load mentor settings
        const userRef = doc(db, 'users', currentUser.uid);
        const userDoc = await getDoc(userRef);
        const userData = userDoc.data();
        const appID = userData?.appID;
        const userTimezone = userData?.timezone || userTimeZone;
        
        const mentorRef = doc(db, 'disciples', discipleId, 'mentorDetails', appID);
        const mentorDoc = await getDoc(mentorRef);
        
        if (mentorDoc.exists()) {
          const mentorData = mentorDoc.data();
          
          // Only set reminder settings from mentor data
          const updatedReminderSettings = {
            ...(mentorData.reminderSettings || {}),
            enabled: mentorData.reminderSettings?.enabled ?? true,
            frequency: mentorData.reminderSettings?.frequency ?? 7,
            preferredTime: mentorData.reminderSettings?.preferredTime ?? "09:00",
            notificationMethod: mentorData.reminderSettings?.notificationMethod ?? 'fcm',
            lastMessageDate: mentorData.reminderSettings?.lastMessageDate ?? null,
            nextReminderDate: mentorData.reminderSettings?.nextReminderDate ?? null,
            quietHours: {
              start: mentorData.reminderSettings?.quietHours?.start ?? "22:00",
              end: mentorData.reminderSettings?.quietHours?.end ?? "08:00"
            }
          };
          
          // Debug: Check the type of frequency from Firestore
          console.log('🔍 Frequency from Firestore:', {
            frequency: updatedReminderSettings.frequency,
            type: typeof updatedReminderSettings.frequency,
            isNumber: typeof updatedReminderSettings.frequency === 'number',
            isString: typeof updatedReminderSettings.frequency === 'string'
          });
          
          // Ensure frequency is a number
          const frequencyNumber = typeof updatedReminderSettings.frequency === 'number' 
            ? updatedReminderSettings.frequency 
            : parseInt(updatedReminderSettings.frequency, 10) || 7;
          
          const finalReminderSettings = {
            ...updatedReminderSettings,
            frequency: frequencyNumber
          };
          
          setReminderSettings(finalReminderSettings);
          setFrequencyInput(frequencyNumber.toString());

          // Load meet in-person reminders settings
          setMeetInPersonReminders(mentorData.meetInPersonRemindersEnabled ?? false);
          setInPersonReminderRhythm(mentorData.inPersonReminderRhythm || 'monthly');

          // Load and display next reminder date
          if (mentorData.reminderSettings?.nextReminderDate) {
            updateNextReminderDisplay(mentorData.reminderSettings.nextReminderDate, userTimezone);
          } else {
            setNextReminderDateWithTime(null);
            setNextReminderTimezoneLabel('');
          }
          
          // Load and display nextNotificationDateAndTime
          if (mentorData.reminderSettings?.nextNotificationDateAndTime) {
            const notificationDateValue = mentorData.reminderSettings.nextNotificationDateAndTime;
            setNextNotificationDateAndTime(notificationDateValue);
            
            // Parse and format for display
            let notificationDate;
            if (notificationDateValue?.toDate && typeof notificationDateValue.toDate === 'function') {
              notificationDate = notificationDateValue.toDate();
            } else if (typeof notificationDateValue === 'string') {
              notificationDate = new Date(notificationDateValue);
            } else {
              notificationDate = new Date(notificationDateValue);
            }
            
            if (!isNaN(notificationDate.getTime())) {
              const dateInTz = toZonedTime(notificationDate, userTimezone);
              const dateStr = format(dateInTz, 'MM/dd/yyyy');
              const timeStr = format(dateInTz, 'h:mm a');
              setNextNotificationDateWithTime(`${dateStr} at ${timeStr}`);
              
              // Set inputs for editing
              const dateInput = format(dateInTz, 'yyyy-MM-dd');
              const timeInput = format(dateInTz, 'HH:mm');
              setNextNotificationDateInput(dateInput);
              setNextNotificationTimeInput(timeInput);
            }
          } else {
            setNextNotificationDateAndTime(null);
            setNextNotificationDateWithTime(null);
          }
        }
      } catch (error) {
        console.error('Error loading data:', error);
        setError('Failed to load settings');
      }
    };

    loadData();
  }, [discipleId, currentUser]);

  // Make sure initialData is coming from the main disciple document
  useEffect(() => {
    if (initialData) {
      setDiscipleData({
        ...initialData,
        reminderSettings: initialData.reminderSettings || {}
      });
    }
  }, [initialData]);

  // Modified update handlers with validation and confirmation
  const handleDiscipleDataUpdate = (field, value) => {
    setPendingChanges(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Add state for input value
  const [frequencyInput, setFrequencyInput] = useState('7');

  // Modify the handleReminderSettingChange function
  const handleReminderSettingChange = async (field, value) => {
    try {
      const mentorRef = await getMentorDocRef();
      const mentorDoc = await getDoc(mentorRef);
      let currentData = mentorDoc.data() || {};

      // Ensure reminderSettings exists with defaults
      if (!currentData.reminderSettings) {
        currentData.reminderSettings = {
          enabled: false,
          frequency: 7,
          preferredTime: "09:00",
          notificationMethod: 'fcm',
          lastMessageDate: null,
          nextReminderDate: null,
          quietHours: {
            start: "22:00",
            end: "08:00"
          }
        };
      }

      // Handle quietHours updates differently
      if (field === 'quietHours') {
        currentData.reminderSettings.quietHours = {
          ...currentData.reminderSettings.quietHours,
          ...value
        };
      } else if (field === 'frequency') {
        // Ensure frequency is always a number
        currentData.reminderSettings[field] = Number(value);
      } else {
        currentData.reminderSettings[field] = value;
      }

      // Update the database
      await updateDoc(mentorRef, {
        reminderSettings: currentData.reminderSettings
      });

        // Update local state
        setReminderSettings(currentData.reminderSettings);
        
        // Update next reminder display if nextReminderDate changed
        if (currentData.reminderSettings?.nextReminderDate) {
          const userRef = doc(db, 'users', currentUser.uid);
          const userDoc = await getDoc(userRef);
          const userData = userDoc.data();
          const userTimezone = userData?.timezone || userTimeZone;
          updateNextReminderDisplay(currentData.reminderSettings.nextReminderDate, userTimezone);
        }
        
    } catch (error) {
      console.error('Error updating reminder settings:', error);
      setError('Failed to update reminder settings');
    }
  };

  // Update the handleFrequencyBlur function
  const handleFrequencyBlur = async () => {
    const numValue = parseInt(frequencyInput, 10);
    if (!isNaN(numValue) && numValue > 0) {
      try {
        const mentorRef = await getMentorDocRef();
        const mentorDoc = await getDoc(mentorRef);
        const currentData = mentorDoc.data() || {};

        // Get the current date as base
        const now = new Date();
        
        // Helper function to safely create a date
        const createValidDate = (dateValue) => {
          if (!dateValue) return null;
          const date = new Date(dateValue);
          return isNaN(date.getTime()) ? null : date;
        };
        
        // Debug: Log the date values we're trying to parse
        console.log('🔍 Date parsing debug:', {
          lastInteractionDate: currentData.reminderSettings?.lastInteractionDate,
          lastMessageDate: currentData.reminderSettings?.lastMessageDate,
          createdAt: currentData.createdAt,
          currentData: currentData
        });
        
        // Try to find a valid base date, fallback to current time
        let baseDate = createValidDate(currentData.reminderSettings?.lastInteractionDate) ||
                      createValidDate(currentData.reminderSettings?.lastMessageDate) ||
                      createValidDate(currentData.createdAt) ||
                      now;
        
        // Ensure baseDate is a valid Date object
        if (!(baseDate instanceof Date) || isNaN(baseDate.getTime())) {
          console.warn('Invalid base date found, using current time');
          baseDate = now;
        }
        
        console.log('🔍 Final baseDate:', baseDate.toISOString());

        // Calculate next reminder date based on new frequency
        const nextDate = new Date(baseDate);
        while (nextDate <= now) {
          nextDate.setDate(nextDate.getDate() + numValue);
        }

        // Default settings structure with updated frequency and next date
        const updatedSettings = {
          ...(currentData.reminderSettings || {}),
          enabled: true,
          frequency: Number(numValue), // Ensure it's a number
          preferredTime: currentData.reminderSettings?.preferredTime || "09:00",
          notificationMethod: currentData.reminderSettings?.notificationMethod || 'fcm',
          lastInteractionDate: baseDate.toISOString(),
          nextReminderDate: nextDate.toISOString(),
          quietHours: {
            start: currentData.reminderSettings?.quietHours?.start || "22:00",
            end: currentData.reminderSettings?.quietHours?.end || "08:00"
          }
        };

        // Update the document
        await updateDoc(mentorRef, {
          reminderSettings: updatedSettings
        });

        // Create the reminder in the queue
        const reminderData = {
          discipleId: discipleId,
          discipleName: discipleData.name,
          mentorId: currentUser.uid,
          message: `Time to connect with ${discipleData.name}`,
          scheduledFor: nextDate,
          queueStatus: 'queued',
          reminderStatus: 'scheduled',
          archived: false,
          frequency: numValue,
          interval: numValue,
          preferredTime: updatedSettings.preferredTime,
          notificationMethod: updatedSettings.notificationMethod,
          attempts: 0,
          messageSent: false,
          notificationsSent: 0,
          createdAt: now,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
        };

        // Add to reminder queue
        const reminderQueueRef = collection(db, 'users', currentUser.uid, 'reminderQueue');
        await addDoc(reminderQueueRef, reminderData);

        // Update local state
        setReminderSettings(updatedSettings);
        setFrequencyInput(numValue.toString());

        // Update next reminder display
        const userRef = doc(db, 'users', currentUser.uid);
        const userDoc = await getDoc(userRef);
        const userData = userDoc.data();
        const userTimezone = userData?.timezone || userTimeZone;
        updateNextReminderDisplay(nextDate.toISOString(), userTimezone);

        console.log('🟢 Updated frequency and created reminder:', {
          frequency: numValue,
          baseDate: baseDate.toISOString(),
          nextReminder: nextDate.toISOString()
        });

      } catch (error) {
        console.error('Error updating frequency:', error);
        setError('Failed to update frequency');
        setFrequencyInput(reminderSettings.frequency?.toString() || '7');
      }
    } else {
      setFrequencyInput(reminderSettings.frequency?.toString() || '7');
      setError('Please enter a valid number greater than 0');
    }
  };

  // Add this helper function at the start of your component
  const getMentorDocRef = async () => {
    if (!currentUser?.uid) {
      console.error("getMentorDocRef: currentUser or currentUser.uid is not available.");
      throw new Error("User information is not available.");
    }
    const userRef = doc(db, 'users', currentUser.uid);
    const userDoc = await getDoc(userRef);
    if (!userDoc.exists()) {
       console.error("getMentorDocRef: User document not found for uid:", currentUser.uid);
       throw new Error("User document not found.");
    }
    const appID = userDoc.data().appID;
    
    // Return the properly constructed reference
    return doc(db, 'disciples', discipleId, 'mentorDetails', appID);
  };

  // Update the applyChange function
  const applyChange = async (change) => {
    if (change.type !== 'reminderSettings' || change.field === 'frequency') {
      // Handle non-reminder settings changes or skip frequency (handled by handleFrequencyBlur)
      return;
    }
    
    try {
      setIsSaving(true);
      const mentorRef = await getMentorDocRef();
      const mentorDoc = await getDoc(mentorRef);
      const currentData = mentorDoc.data() || {};

      // Ensure complete structure
      const currentSettings = {
        enabled: false,
        frequency: 7,
        preferredTime: "09:00",
        notificationMethod: 'fcm',
        lastMessageDate: null,
        nextReminderDate: null,
        quietHours: {
          start: "22:00",
          end: "08:00"
        },
        ...(currentData.reminderSettings || {})
      };

      const updatedSettings = {
        ...currentSettings,
        [change.field]: change.field === 'frequency' ? Number(change.value) : change.value
      };

      await updateDoc(mentorRef, {
        reminderSettings: updatedSettings
      });

      setReminderSettings(updatedSettings);
      setShowConfirmDialog(false);
      setPendingChange(null);

    } catch (error) {
      console.error('Error applying change:', error);
      setError('Failed to save changes');
    } finally {
      setIsSaving(false);
    }
  };

  // Add this function to handle disciple data changes
  const handleDiscipleDataChange = (field, value) => {
    setDiscipleData(prev => ({
      ...prev,
      [field]: value
    }));
    
    setPendingChanges(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Add state for file upload
  const [uploading, setUploading] = useState(false);

  // Add appID state
  const [appID, setAppID] = useState(null);
  
  // Content tracks state
  const [availableTracks, setAvailableTracks] = useState([]);
  const [subscribedTrackIds, setSubscribedTrackIds] = useState([]);
  const [loadingTracks, setLoadingTracks] = useState(false);
  
  // Fetch appID on mount
  useEffect(() => {
    const fetchAppID = async () => {
      try {
        if (!currentUser?.uid) {
          console.warn('fetchAppID: currentUser or currentUser.uid is missing.');
          return;
        }
        console.log('Fetching appID for user:', currentUser?.uid);
        const userRef = doc(db, 'users', currentUser.uid);
        const userDoc = await getDoc(userRef);
        if (userDoc.exists()) {
          const appID = userDoc.data().appID;
          console.log('Found appID:', appID);
          setAppID(appID);
        } else {
          console.warn('No user document found for:', currentUser.uid);
        }
      } catch (error) {
        console.error('Error fetching appID:', error);
      }
    };
    fetchAppID();
  }, [currentUser?.uid]);

  // Fetch available content tracks and subscribed tracks
  useEffect(() => {
    const fetchTracks = async () => {
      if (!discipleId || !appID) return;
      
      setLoadingTracks(true);
      try {
        // Fetch public tracks (includes tracks without organizationId and org tracks marked public)
        const tracksRef = collection(db, 'contentTracks');
        const publicTracksQuery = query(
          tracksRef,
          where('visibility.isPublic', '==', true)
        );
        const publicTracksSnap = await getDocs(publicTracksQuery);
        const publicTracks = publicTracksSnap.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));

        // Fetch user's organization tracks (including org-only tracks)
        if (currentUser?.uid) {
          const userOrgsRef = collection(db, 'users', currentUser.uid, 'organizations');
          const userOrgsSnap = await getDocs(userOrgsRef);
          const orgIds = userOrgsSnap.docs.map(doc => doc.id);
          
          let orgTracks = [];
          if (orgIds.length > 0) {
            const orgTracksQuery = query(
              tracksRef,
              where('organizationId', 'in', orgIds)
            );
            const orgTracksSnap = await getDocs(orgTracksQuery);
            orgTracks = orgTracksSnap.docs.map(doc => ({
              id: doc.id,
              ...doc.data()
            }));
          }
          
          // Combine and deduplicate
          // Include: public tracks (with or without org) + org-specific tracks
          const allTracks = [...publicTracks, ...orgTracks];
          const uniqueTracks = Array.from(
            new Map(allTracks.map(track => [track.id, track])).values()
          );
          setAvailableTracks(uniqueTracks);
        } else {
          // Not logged in - only public tracks
          setAvailableTracks(publicTracks);
        }

        // Fetch subscribed tracks for this disciple
        const subscribedTracksRef = collection(
          db,
          'disciples',
          discipleId,
          'mentorDetails',
          appID,
          'subscribedTracks'
        );
        const subscribedSnap = await getDocs(subscribedTracksRef);
        const subscribedIds = subscribedSnap.docs.map(doc => doc.id);
        setSubscribedTrackIds(subscribedIds);
      } catch (error) {
        console.error('Error fetching tracks:', error);
      } finally {
        setLoadingTracks(false);
      }
    };

    fetchTracks();
  }, [discipleId, appID, currentUser?.uid]);

  // Handle track subscription
  const handleTrackSubscribe = async (trackId) => {
    if (!discipleId || !appID) return;
    
    try {
      const mentorDetailsRef = doc(db, 'disciples', discipleId, 'mentorDetails', appID);
      
      // Store in subcollection (for multiple tracks support)
      const subscribedTrackRef = doc(
        db,
        'disciples',
        discipleId,
        'mentorDetails',
        appID,
        'subscribedTracks',
        trackId
      );
      
      await setDoc(subscribedTrackRef, {
        subscribedAt: new Date(),
        progress: 0,
        lastAccessed: new Date()
      });

      // ALSO set subscribedContentTrackId field (for mobile app compatibility and primary track)
      // This ensures the mobile app and DiscipleMenu can find the active track
      // If there was a previous track, this replaces it (mobile app supports one track at a time)
      const mentorDetailsDoc = await getDoc(mentorDetailsRef);
      const previousTrackId = mentorDetailsDoc.exists() ? mentorDetailsDoc.data().subscribedContentTrackId : null;
      
      await updateDoc(mentorDetailsRef, {
        subscribedContentTrackId: trackId,
      });
      
      // If there was a previous track and it's different, we've replaced it
      // (The old track remains in subscribedTracks subcollection for history, but subscribedContentTrackId points to the new one)

      // Initialize content track progress
      const trackDoc = await getDoc(doc(db, 'contentTracks', trackId));
      if (trackDoc.exists()) {
        // Load resources to calculate initial progress
        const resourcesRef = collection(db, 'contentTracks', trackId, 'resources');
        const resourcesSnap = await getDocs(resourcesRef);
        const publishedResources = resourcesSnap.docs
          .map(d => ({ id: d.id, ...d.data() }))
          .filter(r => r.status === 'published' || !r.status);
        
        const initialProgress = {
          trackId,
          totalPublishedResources: publishedResources.length,
          sentResourceIds: [],
          sentCount: 0,
          remainingCount: publishedResources.length,
          completed: false,
          lastUpdated: new Date(),
        };
        
        await updateDoc(mentorDetailsRef, {
          contentTrackProgress: initialProgress,
        });
      }

      // Update track stats
      await updateDoc(doc(db, 'contentTracks', trackId), {
        'stats.totalSubscribers': increment(1)
      });

      setSubscribedTrackIds(prev => [...prev, trackId]);
    } catch (error) {
      console.error('Error subscribing to track:', error);
      alert('Failed to subscribe to track. Please try again.');
    }
  };

  // Handle track unsubscription
  const handleTrackUnsubscribe = async (trackId) => {
    if (!discipleId || !appID) return;
    
    try {
      const mentorDetailsRef = doc(db, 'disciples', discipleId, 'mentorDetails', appID);
      
      // Remove from subcollection
      const subscribedTrackRef = doc(
        db,
        'disciples',
        discipleId,
        'mentorDetails',
        appID,
        'subscribedTracks',
        trackId
      );
      
      await deleteDoc(subscribedTrackRef);

      // If this was the primary track (subscribedContentTrackId), clear it
      const mentorDetailsDoc = await getDoc(mentorDetailsRef);
      if (mentorDetailsDoc.exists() && mentorDetailsDoc.data().subscribedContentTrackId === trackId) {
        await updateDoc(mentorDetailsRef, {
          subscribedContentTrackId: null,
          contentTrackProgress: null,
        });
      }

      // Update track stats
      await updateDoc(doc(db, 'contentTracks', trackId), {
        'stats.totalSubscribers': increment(-1)
      });

      setSubscribedTrackIds(prev => prev.filter(id => id !== trackId));
    } catch (error) {
      console.error('Error unsubscribing from track:', error);
      alert('Failed to unsubscribe from track. Please try again.');
    }
  };

  // Simplify the photo upload handler to just handle the URL
  const handlePhotoUpload = async (url) => {
    try {
      console.log('Photo upload success, updating disciple data with URL:', url);
      
      // Update local state first for immediate UI feedback
      setDiscipleData(prev => ({
        ...prev,
        photoURL: url
      }));
      
      // Update Firestore - BOTH the disciple doc AND mentorDetails
      const discipleRef = doc(db, 'disciples', discipleId);
      const mentorRef = doc(db, 'disciples', discipleId, 'mentorDetails', appID);
      
      // Update both documents in parallel
      await Promise.all([
        updateDoc(discipleRef, {
          photoURL: url
        }),
        updateDoc(mentorRef, {
          photoURL: url
        })
      ]);
      
      console.log('Firestore updates complete');
      if (onUpdate) onUpdate();
      
    } catch (error) {
      console.error('Error updating photo:', error);
      setError('Failed to save photo URL');
      // Revert local state if save failed
      setDiscipleData(prev => ({
        ...prev,
        photoURL: initialData.photoURL
      }));
    }
  };

  // Add this helper function to your component
  const formatReminderDate = (dateString) => {
    if (!dateString) return null;
    
    try {
      // Create a date object directly from the string
      const reminderDate = new Date(dateString);
      const now = new Date();
      
      // Check if it's a valid date
      if (isNaN(reminderDate.getTime())) return null;
      
      const formattedDate = reminderDate.toLocaleString([], {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        timeZoneName: 'short'
      });

      if (reminderDate > now) {
        return `Next reminder: ${formattedDate}`;
      } else {
        return `Last reminder was on ${formattedDate}`;
      }
    } catch (error) {
      console.error('Error formatting date:', error);
      return null;
    }
  };

  // When first creating a mentorDetails document
  const createMentorDetails = async (discipleId, mentorId) => {
    const mentorRef = doc(db, 'disciples', discipleId, 'mentorDetails', mentorId);
    
    // Calculate nextInPersonReminderDate: one month from today (default monthly rhythm)
    const nextInPersonReminderDate = new Date();
    nextInPersonReminderDate.setMonth(nextInPersonReminderDate.getMonth() + 1);
    
    const initialData = {
      createdAt: new Date().toISOString(),
      lastMessageDate: null,
      reminderSettings: {
        enabled: true,
        frequency: 7, // default to weekly
        preferredTime: "10:00",
        nextReminderDate: null,
        notificationMethod: "fcm",
        quietHours: {
          start: "23:00",
          end: "08:00"
        }
      },
      meetInPersonRemindersEnabled: false,
      inPersonReminderRhythm: 'monthly',
      nextInPersonReminderDate: nextInPersonReminderDate
    };
    
    await setDoc(mentorRef, initialData);
    return initialData;
  };

  // Add new state for archive confirmation
  const [showArchiveConfirm, setShowArchiveConfirm] = useState(false);

  // Add archive handler
  const handleArchive = async () => {
    try {
      setIsSaving(true);
      const mentorRef = await getMentorDocRef();
      await updateDoc(mentorRef, {
        archived: true
      });
      onUpdate?.(); // Notify parent component of the change
      onClose();
    } catch (error) {
      console.error('Error archiving disciple:', error);
      setError('Failed to archive disciple');
    } finally {
      setIsSaving(false);
    }
  };

  // Add this function to update next reminder date after sending a message
  const updateNextReminderAfterMessage = async (discipleId, userAppID) => {
    try {
      const mentorRef = doc(db, 'disciples', discipleId, 'mentorDetails', userAppID);
      const mentorDoc = await getDoc(mentorRef);
      const currentData = mentorDoc.data();

      if (currentData?.reminderSettings?.enabled) {
        const now = new Date();
        const nextDate = new Date(now);
        nextDate.setDate(now.getDate() + currentData.reminderSettings.frequency);

        await updateDoc(mentorRef, {
          'reminderSettings.lastMessageDate': now.toISOString(),
          'reminderSettings.nextReminderDate': nextDate.toISOString()
        });
      }
    } catch (error) {
      console.error('Error updating next reminder date:', error);
    }
  };

  // Handle meet in-person reminder settings change
  const handleMeetInPersonChange = async (enabled) => {
    setMeetInPersonReminders(enabled);
    try {
      const mentorRef = await getMentorDocRef();
      const mentorDoc = await getDoc(mentorRef);
      
      if (!mentorDoc.exists()) {
        // Create mentor details if they don't exist
        const userRef = doc(db, 'users', currentUser.uid);
        const userDoc = await getDoc(userRef);
        const appID = userDoc.data()?.appID || currentUser.uid;
        
        // Calculate nextInPersonReminderDate: one month from today (default monthly rhythm)
        const nextInPersonReminderDate = new Date();
        nextInPersonReminderDate.setMonth(nextInPersonReminderDate.getMonth() + 1);
        
        await setDoc(mentorRef, {
          appID: appID,
          name: currentUser.name || '',
          meetInPersonRemindersEnabled: enabled,
          inPersonReminderRhythm: enabled ? inPersonReminderRhythm : 'monthly',
          nextInPersonReminderDate: nextInPersonReminderDate,
          addedAt: new Date(),
        });
      } else {
        const updateData = {
          meetInPersonRemindersEnabled: enabled,
        };
        
        // If enabling, set the rhythm and calculate next date
        if (enabled) {
          updateData.inPersonReminderRhythm = inPersonReminderRhythm;
          const nextInPersonReminderDate = new Date();
          if (inPersonReminderRhythm === 'weekly') {
            nextInPersonReminderDate.setDate(nextInPersonReminderDate.getDate() + 7);
          } else if (inPersonReminderRhythm === 'bi-weekly') {
            nextInPersonReminderDate.setDate(nextInPersonReminderDate.getDate() + 14);
          } else {
            nextInPersonReminderDate.setMonth(nextInPersonReminderDate.getMonth() + 1);
          }
          updateData.nextInPersonReminderDate = nextInPersonReminderDate;
        }
        
        await updateDoc(mentorRef, updateData);
      }
    } catch (error) {
      console.error('Error updating meet in-person reminders:', error);
      setError('Failed to update meet in-person reminders');
      // Revert state on error
      setMeetInPersonReminders(!enabled);
    }
  };

  // Handle in-person reminder rhythm change
  const handleInPersonRhythmChange = async (rhythm) => {
    setInPersonReminderRhythm(rhythm);
    try {
      const mentorRef = await getMentorDocRef();
      const mentorDoc = await getDoc(mentorRef);
      
      if (mentorDoc.exists()) {
        // Calculate next date based on rhythm
        const nextInPersonReminderDate = new Date();
        if (rhythm === 'weekly') {
          nextInPersonReminderDate.setDate(nextInPersonReminderDate.getDate() + 7);
        } else if (rhythm === 'bi-weekly') {
          nextInPersonReminderDate.setDate(nextInPersonReminderDate.getDate() + 14);
        } else {
          nextInPersonReminderDate.setMonth(nextInPersonReminderDate.getMonth() + 1);
        }
        
        await updateDoc(mentorRef, {
          inPersonReminderRhythm: rhythm,
          nextInPersonReminderDate: nextInPersonReminderDate,
        });
      }
    } catch (error) {
      console.error('Error updating in-person reminder rhythm:', error);
      setError('Failed to update reminder rhythm');
      // Revert state on error
      setInPersonReminderRhythm('monthly');
    }
  };

  // Update the save handler
  const handleSave = async () => {
    setIsSaving(true);
    try {
      // Get reference to the correct location: disciples/discipleId/mentorDetails/currentUser.appID
      const mentorDetailsRef = doc(
        db, 
        'disciples', 
        discipleId, 
        'mentorDetails', 
        currentUser.appID
      );

      // Get existing data to compare old vs new values
      const mentorDetailsDoc = await getDoc(mentorDetailsRef);
      const existingData = mentorDetailsDoc.exists() ? mentorDetailsDoc.data() : {};
      const existingReminderSettings = existingData?.reminderSettings || {};
      const oldFrequency = existingReminderSettings.frequency || 7;
      const oldPreferredTime = existingReminderSettings.preferredTime || '09:00';
      
      // Get user timezone
      const userRef = doc(db, 'users', currentUser.uid);
      const userDoc = await getDoc(userRef);
      const userData = userDoc.data();
      const userTimezone = userData?.timezone || userTimeZone;

      // Get current frequency from frequencyInput (the actual input value) instead of reminderSettings.frequency
      // This ensures we use the latest value even if handleFrequencyBlur hasn't fired
      const currentFrequency = parseInt(frequencyInput, 10) || reminderSettings.frequency || 7;
      
      // Track if we need to recalculate dates
      const frequencyChanged = oldFrequency !== currentFrequency;
      const preferredTimeChanged = oldPreferredTime !== reminderSettings.preferredTime;

      console.log(`[DiscipleSettings] Save: oldFrequency=${oldFrequency}, newFrequency=${currentFrequency} (from frequencyInput=${frequencyInput}), frequencyChanged=${frequencyChanged}`);
      console.log(`[DiscipleSettings] Save: oldPreferredTime=${oldPreferredTime}, newPreferredTime=${reminderSettings.preferredTime}, preferredTimeChanged=${preferredTimeChanged}`);

      // Build complete reminderSettings object (preserve existing fields, update changed ones)
      const updatedReminderSettings = {
        ...existingReminderSettings, // Preserve all existing fields (quietHours, lastMessageDate, etc.)
        enabled: reminderSettings.enabled,
        frequency: currentFrequency, // Use currentFrequency from frequencyInput
        preferredTime: reminderSettings.preferredTime,
      };

      // If frequency changed, recalculate nextReminderDate with timezone-aware logic
      // Prefer lastNotificationDateAndTime (most accurate), then lastReminderSent, then lastMessageDate, then today
      if (frequencyChanged || !existingReminderSettings.nextReminderDate) {
        console.log(`[DiscipleSettings] Recalculating dates because frequencyChanged=${frequencyChanged} or !nextReminderDate=${!existingReminderSettings.nextReminderDate}`);
        // Use lastNotificationDateAndTime if available (most accurate), otherwise fall back to lastReminderSent, then lastMessageDate, then today
        let baseDate;
        
        if (existingReminderSettings.lastNotificationDateAndTime) {
          // Prefer lastNotificationDateAndTime (most accurate)
          const lastNotificationDate = existingReminderSettings.lastNotificationDateAndTime;
          baseDate = typeof lastNotificationDate === 'string' 
            ? new Date(lastNotificationDate)
            : (lastNotificationDate?.toDate ? lastNotificationDate.toDate() : new Date(lastNotificationDate));
        } else if (existingReminderSettings.lastReminderSent) {
          // Fall back to lastReminderSent
          const lastSent = existingReminderSettings.lastReminderSent;
          baseDate = lastSent?.toDate ? lastSent.toDate() : new Date(lastSent);
        } else if (existingReminderSettings.lastMessageDate) {
          // Fall back to lastMessageDate
          const lastMessage = existingReminderSettings.lastMessageDate;
          baseDate = lastMessage?.toDate ? lastMessage.toDate() : new Date(lastMessage);
        } else {
          // No history, use today
          baseDate = new Date();
        }
        
        // Calculate with timezone-aware logic (matching backend)
        let calculatedDate = calculateNextReminderDateWithTimezone(
          baseDate,
          reminderSettings.preferredTime,
          userTimezone,
          currentFrequency // Use currentFrequency from frequencyInput
        );
        
        // Ensure date is in the future
        const now = new Date();
        if (calculatedDate <= now) {
          // If calculated date is in past, recalculate from today
          calculatedDate = calculateNextReminderDateWithTimezone(
            now,
            reminderSettings.preferredTime,
            userTimezone,
            currentFrequency // Use currentFrequency from frequencyInput
          );
        }
        
        updatedReminderSettings.nextReminderDate = calculatedDate.toISOString();
        
        // Also set nextNotificationDateAndTime to the same date with preferredTime
        const notificationDateWithTime = calculateNextReminderDateWithTimezone(
          calculatedDate,
          reminderSettings.preferredTime,
          userTimezone,
          0 // Don't add days, just apply the time
        );
        updatedReminderSettings.nextNotificationDateAndTime = notificationDateWithTime.toISOString();
        
        console.log(`Updated nextReminderDate and nextNotificationDateAndTime with new frequency: ${currentFrequency} (was ${oldFrequency})`);
      }
      
      // If preferredTime changed (and frequency didn't change), update nextNotificationDateAndTime based on current nextReminderDate
      if (preferredTimeChanged && !frequencyChanged && existingReminderSettings.nextReminderDate) {
        // Use existing nextReminderDate and just update the time
        const nextReminderDateValue = existingReminderSettings.nextReminderDate;
        let dateToRecalculate;
        
        if (nextReminderDateValue?.toDate) {
          dateToRecalculate = nextReminderDateValue.toDate();
        } else if (typeof nextReminderDateValue === 'string') {
          dateToRecalculate = new Date(nextReminderDateValue);
        } else {
          dateToRecalculate = new Date(nextReminderDateValue);
        }
        
        // Recalculate nextReminderDate with new preferredTime (same date, new time)
        const recalculatedDate = calculateNextReminderDateWithTimezone(
          dateToRecalculate,
          reminderSettings.preferredTime,
          userTimezone,
          0 // Keep same date, just apply preferredTime
        );
        updatedReminderSettings.nextReminderDate = recalculatedDate.toISOString();
        
        // Update nextNotificationDateAndTime with the new preferredTime
        const notificationDateWithTime = calculateNextReminderDateWithTimezone(
          recalculatedDate,
          reminderSettings.preferredTime,
          userTimezone,
          0 // Don't add days, just apply the time
        );
        updatedReminderSettings.nextNotificationDateAndTime = notificationDateWithTime.toISOString();
        
        console.log(`Updated nextReminderDate and nextNotificationDateAndTime with new preferredTime: ${reminderSettings.preferredTime} (was ${oldPreferredTime}) in timezone: ${userTimezone}`);
      } else if (preferredTimeChanged && !frequencyChanged && !existingReminderSettings.nextReminderDate) {
        // PreferredTime changed but no nextReminderDate exists - calculate from today
        const calculatedDate = calculateNextReminderDateWithTimezone(
          new Date(),
          reminderSettings.preferredTime,
          userTimezone,
          currentFrequency // Use currentFrequency from frequencyInput
        );
        
        // Ensure date is in the future
        const now = new Date();
        let finalDate = calculatedDate;
        if (finalDate <= now) {
          finalDate = calculateNextReminderDateWithTimezone(
            now,
            reminderSettings.preferredTime,
            userTimezone,
            currentFrequency // Use currentFrequency from frequencyInput
          );
        }
        
        updatedReminderSettings.nextReminderDate = finalDate.toISOString();
        
        const notificationDateWithTime = calculateNextReminderDateWithTimezone(
          finalDate,
          reminderSettings.preferredTime,
          userTimezone,
          0 // Don't add days, just apply the time
        );
        updatedReminderSettings.nextNotificationDateAndTime = notificationDateWithTime.toISOString();
        
        console.log(`Updated nextReminderDate and nextNotificationDateAndTime with new preferredTime: ${reminderSettings.preferredTime} (was ${oldPreferredTime}) - calculated from today`);
      }

      // Prepare the update data
      const updateData = {
        // Mentor-specific fields
        lastInteractionAt: serverTimestamp(),
        reminderSettings: updatedReminderSettings, // Use nested object, not dot notation
        // Meet in-person reminders
        meetInPersonRemindersEnabled: meetInPersonReminders,
        inPersonReminderRhythm: meetInPersonReminders ? inPersonReminderRhythm : 'monthly',
      };

      // Calculate nextInPersonReminderDate if enabled
      if (meetInPersonReminders) {
        const nextInPersonReminderDate = new Date();
        if (inPersonReminderRhythm === 'weekly') {
          nextInPersonReminderDate.setDate(nextInPersonReminderDate.getDate() + 7);
        } else if (inPersonReminderRhythm === 'bi-weekly') {
          nextInPersonReminderDate.setDate(nextInPersonReminderDate.getDate() + 14);
        } else {
          nextInPersonReminderDate.setMonth(nextInPersonReminderDate.getMonth() + 1);
        }
        updateData.nextInPersonReminderDate = nextInPersonReminderDate;
      }

      // Update the document
      await updateDoc(mentorDetailsRef, updateData);

      // Also update disciple document with isChild and hasDevice
      const discipleRef = doc(db, 'disciples', discipleId);
      const discipleUpdateData = {};
      
      if (isChild !== null && isChild !== undefined) {
        discipleUpdateData.isChild = isChild === true;
        if (isChild === true) {
          discipleUpdateData.hasDevice = hasDevice === true;
        } else {
          // If not a child, remove hasDevice field
          discipleUpdateData.hasDevice = null;
        }
      }
      
      // Only update if there are changes
      if (Object.keys(discipleUpdateData).length > 0) {
        await updateDoc(discipleRef, discipleUpdateData);
      }

      // Update next reminder display if nextReminderDate is in the update
      const updatedNextReminderDate = updatedReminderSettings.nextReminderDate;
      if (updatedNextReminderDate) {
        const dateForDisplay = typeof updatedNextReminderDate === 'string' 
          ? new Date(updatedNextReminderDate)
          : updatedNextReminderDate;
        updateNextReminderDisplay(dateForDisplay, userTimezone);
      }

      // Update local state
      setReminderSettings(updatedReminderSettings);

      if (onUpdate) {
        onUpdate(updateData);
      }

      onClose();
    } catch (error) {
      console.error('Error saving disciple settings:', error);
      setError('Failed to save settings. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  // Remove the modal wrapper and overlay. Only render the content card and its children.
  if (!isOpen) {
    return null; // Render nothing if the modal shouldn't be open
  }

  // Main return statement (only renders if isOpen is true)
  return (
    <div className="flex flex-col h-full w-full max-w-xl mx-auto">
      {/* Main Content: Make scrollable and fill available height */}
      <div className="flex-1 p-6 overflow-y-auto pb-32" style={{ minHeight: 0 }}>
        {/* Action Buttons Row at the Top (without Archive) */}
        <div className="flex justify-end items-center mb-4">
          <div className="flex space-x-3">
            <button
              onClick={() => {
                if (typeof onClose === 'function') {
                  onClose();
                }
              }}
              className="px-4 py-2 text-gray-600 hover:text-gray-800 border border-gray-300 rounded-md"
              disabled={isSaving}
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-blue-300"
              disabled={isSaving}
            >
              {isSaving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>

        {error && (
          <div className="mb-3 p-3 bg-red-50 text-red-700 rounded-md">
            {error}
          </div>
        )}

        {/* Contact Information */}
        <div className="bg-gray-50 rounded-lg p-4 mb-4">
          <h3 className="text-lg font-semibold text-gray-900 mb-1">Contact Information</h3>
          <p className="text-sm text-gray-600 mb-4">Manage the disciple's contact details and personal information.</p>
          
          <div className="space-y-3">
            <div className="flex items-center gap-4">
              <label className="text-sm font-medium text-gray-700 w-24 flex-shrink-0">Name</label>
              <input
                type="text"
                value={discipleData.name}
                onChange={(e) => handleDiscipleDataChange('name', e.target.value)}
                className="flex-1 p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                disabled={isSaving}
              />
            </div>
            <div className="flex items-center gap-4">
              <label className="text-sm font-medium text-gray-700 w-24 flex-shrink-0">Email</label>
              <input
                type="email"
                value={discipleData.email}
                onChange={(e) => handleDiscipleDataChange('email', e.target.value)}
                className="flex-1 p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                disabled={isSaving}
              />
            </div>
            <div className="flex items-center gap-4">
              <label className="text-sm font-medium text-gray-700 w-24 flex-shrink-0">Phone</label>
              <input
                type="tel"
                value={discipleData.phone}
                onChange={(e) => handleDiscipleDataChange('phone', e.target.value)}
                className="flex-1 p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                disabled={isSaving}
              />
            </div>
            <div className="flex items-center gap-4">
              <label className="text-sm font-medium text-gray-700 w-24 flex-shrink-0">Birthday</label>
              <input
                type="date"
                value={discipleData.birthday}
                onChange={(e) => handleDiscipleDataChange('birthday', e.target.value)}
                className="flex-1 p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                disabled={isSaving}
              />
            </div>
            
            {/* Is Child Checkbox */}
            <div className="flex items-start gap-3">
              <input
                type="checkbox"
                checked={isChild}
                onChange={(e) => {
                  const newIsChild = e.target.checked;
                  setIsChild(newIsChild);
                  if (!newIsChild) {
                    setHasDevice(false);
                  }
                }}
                className="mt-1 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                disabled={isSaving}
              />
              <label className="text-sm font-medium text-gray-700 cursor-pointer">
                Is this a child?
              </label>
            </div>

            {/* Has Device Checkbox - Only show if isChild is true */}
            {isChild && (
              <div className="flex items-start gap-3 ml-7">
                <input
                  type="checkbox"
                  checked={hasDevice}
                  onChange={(e) => setHasDevice(e.target.checked)}
                  className="mt-1 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  disabled={isSaving}
                />
                <div className="flex-1">
                  <label className="text-sm font-medium text-gray-700 cursor-pointer block">
                    Do they have a wi-fi device?
                  </label>
                  <p className="text-xs text-gray-500 mt-1">
                    If unchecked, you can use the "Read Together" feature to read content directly to the child.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Discipleship Program */}
        {discipleId && (
          <div className="bg-gray-50 rounded-lg p-4 mb-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-1">Discipleship Program</h3>
            <p className="text-sm text-gray-600 mb-4">View and manage this disciple&apos;s full program — verses, notes, Listen Connect & Help, and progress.</p>
            <Link
              to={`/view-disciple/${discipleId}`}
              className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-md hover:bg-blue-700 text-sm font-medium"
            >
              Open Discipleship Program
              <ArrowTopRightOnSquareIcon className="h-4 w-4" />
            </Link>
          </div>
        )}

        {/* Reminder Settings */}
        <div className="bg-gray-50 rounded-lg p-4 mb-4">
          <div className="flex items-start justify-between mb-4">
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-gray-900 mb-1">Reminders</h3>
              <p className="text-sm text-gray-600">
                Get automated reminders to connect with this disciple.
              </p>
            </div>
            <div className="flex-shrink-0 ml-4 pt-1">
              <Switch
                checked={reminderSettings?.enabled || false}
                onChange={(checked) => handleReminderSettingChange('enabled', checked)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200
                  ${reminderSettings?.enabled ? 'bg-blue-600' : 'bg-gray-200'}`}
              >
                <span className="sr-only">Enable Reminders</span>
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition
                    ${reminderSettings?.enabled ? 'translate-x-6' : 'translate-x-1'}`}
                />
              </Switch>
            </div>
          </div>

          {/* Only show message and settings if reminders are enabled */}
          {reminderSettings?.enabled && (
            <div className="space-y-3 mt-4">
              <div className="flex items-center gap-4">
                <label className="text-sm font-medium text-gray-700 w-32 flex-shrink-0">Frequency (days)</label>
                <input
                  type="number"
                  min="1"
                  value={frequencyInput}
                  onChange={(e) => setFrequencyInput(e.target.value)}
                  onBlur={handleFrequencyBlur}
                  className="w-24 p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  disabled={isSaving}
                />
              </div>

              <div className="flex items-center gap-4">
                <label className="text-sm font-medium text-gray-700 w-32 flex-shrink-0">Preferred Time</label>
                <input
                  type="time"
                  value={reminderSettings.preferredTime}
                  onChange={(e) => handleReminderSettingChange('preferredTime', e.target.value)}
                  className="w-32 p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  disabled={isSaving}
                />
              </div>

              <div className="pt-2">
                <label className="text-sm font-medium text-gray-700 mb-2 block">Quiet Hours</label>
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2 flex-1">
                    <label className="text-xs text-gray-600 w-12">Start</label>
                    <input
                      type="time"
                      value={reminderSettings.quietHours.start}
                      onChange={(e) => handleReminderSettingChange('quietHours', {
                        ...reminderSettings.quietHours,
                        start: e.target.value
                      })}
                      className="flex-1 p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      disabled={isSaving}
                    />
                  </div>
                  <div className="flex items-center gap-2 flex-1">
                    <label className="text-xs text-gray-600 w-12">End</label>
                    <input
                      type="time"
                      value={reminderSettings.quietHours.end}
                      onChange={(e) => handleReminderSettingChange('quietHours', {
                        ...reminderSettings.quietHours,
                        end: e.target.value
                      })}
                      className="flex-1 p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      disabled={isSaving}
                    />
                  </div>
                </div>
              </div>

              {/* Next Reminder Date Display - Only show when reminders are enabled */}
              {nextReminderDateWithTime && (
                <div className="mt-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <div className="flex flex-col">
                    <label className="text-xs font-medium text-gray-500 mb-1">Next Reminder</label>
                    <span className="text-sm font-medium text-gray-900">{nextReminderDateWithTime}</span>
                    {nextReminderTimezoneLabel && (
                      <span className="text-xs text-gray-500 mt-0.5">{nextReminderTimezoneLabel}</span>
                    )}
                  </div>
                </div>
              )}

              {/* Next Notification Date and Time Display - Only show when reminders are enabled */}
              {nextNotificationDateWithTime && (
                <div className="mt-3 p-3 bg-green-50 rounded-lg border border-green-200">
                  <div className="flex items-center justify-between">
                    <div className="flex flex-col flex-1">
                      <label className="text-xs font-medium text-gray-500 mb-1">Next Notification</label>
                      <span className="text-sm font-medium text-gray-900">{nextNotificationDateWithTime}</span>
                      {nextReminderTimezoneLabel && (
                        <span className="text-xs text-gray-500 mt-0.5">{nextReminderTimezoneLabel}</span>
                      )}
                    </div>
                    <button
                      onClick={() => setShowNextNotificationDatePicker(true)}
                      className="ml-3 p-2 text-green-600 hover:text-green-800 hover:bg-green-100 rounded-md transition-colors"
                      disabled={isSaving}
                      title="Edit next notification date and time"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Meet In-Person Reminders Section */}
        <div className="bg-gray-50 rounded-lg p-4 mb-4">
          <div className="flex items-start justify-between mb-4">
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-gray-900 mb-1">In-Person Reminders</h3>
              <p className="text-sm text-gray-600">
                Get reminders to meet with this disciple in person (if they are local to you).
              </p>
            </div>
            <div className="flex-shrink-0 ml-4 pt-1">
              <Switch
                checked={meetInPersonReminders}
                onChange={handleMeetInPersonChange}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200
                  ${meetInPersonReminders ? 'bg-blue-600' : 'bg-gray-200'}`}
              >
                <span className="sr-only">Enable In-Person Reminders</span>
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition
                    ${meetInPersonReminders ? 'translate-x-6' : 'translate-x-1'}`}
                />
              </Switch>
            </div>
          </div>

          {meetInPersonReminders && (
            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Reminder Rhythm</label>
              <p className="text-xs text-gray-500 mb-3">
                How often to receive in-person meeting reminders
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => handleInPersonRhythmChange('weekly')}
                  className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                    inPersonReminderRhythm === 'weekly'
                      ? 'bg-blue-600 text-white'
                      : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                  }`}
                  disabled={isSaving}
                >
                  Weekly
                </button>
                <button
                  onClick={() => handleInPersonRhythmChange('bi-weekly')}
                  className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                    inPersonReminderRhythm === 'bi-weekly'
                      ? 'bg-blue-600 text-white'
                      : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                  }`}
                  disabled={isSaving}
                >
                  Bi-Weekly
                </button>
                <button
                  onClick={() => handleInPersonRhythmChange('monthly')}
                  className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                    inPersonReminderRhythm === 'monthly'
                      ? 'bg-blue-600 text-white'
                      : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                  }`}
                  disabled={isSaving}
                >
                  Monthly
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Content Tracks Subscription */}
        <div className="bg-gray-50 rounded-lg p-4 mb-4">
          <h3 className="text-lg font-semibold text-gray-900 mb-1">Content Tracks</h3>
          <p className="text-sm text-gray-600 mb-4">
            Subscribe this disciple to available content tracks for their spiritual growth journey.
          </p>
            
            {loadingTracks ? (
              <div className="text-center py-4">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500 mx-auto"></div>
                <p className="mt-2 text-sm text-gray-500">Loading tracks...</p>
              </div>
            ) : availableTracks.length === 0 ? (
              <p className="text-sm text-gray-500">No content tracks available. Create public tracks to make them available here.</p>
            ) : (
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {availableTracks.map(track => {
                  const isSubscribed = subscribedTrackIds.includes(track.id);
                  return (
                    <div
                      key={track.id}
                      className="flex items-center justify-between p-3 bg-white rounded border border-gray-200"
                    >
                      <div className="flex-1">
                        <h4 className="text-sm font-medium text-gray-900">{track.name}</h4>
                        {track.description && (
                          <p className="text-xs text-gray-500 mt-1 line-clamp-1">{track.description}</p>
                        )}
                      </div>
                      <button
                        onClick={() => isSubscribed ? handleTrackUnsubscribe(track.id) : handleTrackSubscribe(track.id)}
                        className={`ml-4 px-3 py-1 text-xs font-medium rounded ${
                          isSubscribed
                            ? 'bg-green-100 text-green-800 hover:bg-green-200'
                            : 'bg-blue-100 text-blue-800 hover:bg-blue-200'
                        }`}
                        disabled={isSaving}
                      >
                        {isSubscribed ? 'Subscribed' : 'Subscribe'}
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
        </div>

        {/* Archive Disciple Section */}
        <div className="bg-gray-50 rounded-lg p-4 mb-4">
          <h3 className="text-lg font-semibold text-gray-900 mb-1">Archive Disciple</h3>
          <p className="text-sm text-gray-600 mb-4">
            Archive this disciple to move them out of your active disciples list. You can unarchive them at any time.
          </p>
          <div className="flex justify-start">
            <button
              onClick={() => setShowArchiveConfirm(true)}
              className="px-4 py-2 text-sm font-medium text-red-600 hover:text-red-800 border border-red-300 rounded-md hover:bg-red-50 transition-colors"
              disabled={isSaving}
            >
              Archive Disciple
            </button>
          </div>
        </div>
      </div>

      {/* Confirmation Dialogs */}
      {showConfirmDialog && pendingChange && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">Confirm Change</h3>
            <p className="mb-6 text-gray-600">{pendingChange.message}</p>
            <div className="flex justify-end space-x-4">
              <button
                onClick={() => {
                  setShowConfirmDialog(false);
                  setPendingChange(null);
                  // Reset frequency input to current value if canceling
                  if (pendingChange.field === 'frequency') {
                    setFrequencyInput(reminderSettings.frequency?.toString() || '7');
                  }
                }}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
              >
                Cancel
              </button>
              <button
                onClick={() => applyChange(pendingChange)}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      {showArchiveConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">Confirm Archive</h3>
            <p className="mb-6 text-gray-600">
              Are you sure you want to archive this disciple? They will be moved to your archived disciples list.
            </p>
            <div className="flex justify-end space-x-4">
              <button
                onClick={() => setShowArchiveConfirm(false)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
              >
                Cancel
              </button>
              <button
                onClick={handleArchive}
                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
              >
                Archive
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Next Notification Date and Time Picker Modal */}
      {showNextNotificationDatePicker && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">Edit Next Notification Date and Time</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
                <input
                  type="date"
                  value={nextNotificationDateInput}
                  onChange={(e) => setNextNotificationDateInput(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-green-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Time</label>
                <input
                  type="time"
                  value={nextNotificationTimeInput}
                  onChange={(e) => setNextNotificationTimeInput(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-green-500"
                />
              </div>
              {nextReminderTimezoneLabel && (
                <p className="text-xs text-gray-500">Timezone: {nextReminderTimezoneLabel}</p>
              )}
            </div>
            <div className="flex justify-end space-x-4 mt-6">
              <button
                onClick={() => {
                  setShowNextNotificationDatePicker(false);
                  // Reset inputs to current values (synchronously from state)
                  if (nextNotificationDateAndTime) {
                    const notificationDate = typeof nextNotificationDateAndTime === 'string'
                      ? new Date(nextNotificationDateAndTime)
                      : (nextNotificationDateAndTime?.toDate ? nextNotificationDateAndTime.toDate() : new Date(nextNotificationDateAndTime));
                    if (!isNaN(notificationDate.getTime())) {
                      const dateInTz = toZonedTime(notificationDate, userTimeZone);
                      setNextNotificationDateInput(format(dateInTz, 'yyyy-MM-dd'));
                      setNextNotificationTimeInput(format(dateInTz, 'HH:mm'));
                    }
                  }
                }}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
                disabled={isSaving}
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  try {
                    setIsSaving(true);
                    
                    // Get user timezone
                    const userRef = doc(db, 'users', currentUser.uid);
                    const userDoc = await getDoc(userRef);
                    const userData = userDoc.data();
                    const userTimezone = userData?.timezone || userTimeZone;
                    
                    // Combine date and time inputs
                    const dateTimeString = `${nextNotificationDateInput}T${nextNotificationTimeInput}`;
                    const localDate = new Date(dateTimeString);
                    
                    // Convert from user's timezone to UTC
                    const utcDate = fromZonedTime(localDate, userTimezone);
                    
                    // Update in Firestore
                    const mentorRef = await getMentorDocRef();
                    const mentorDoc = await getDoc(mentorRef);
                    const currentData = mentorDoc.data() || {};
                    
                    const updatedSettings = {
                      ...(currentData.reminderSettings || {}),
                      nextNotificationDateAndTime: utcDate.toISOString(),
                    };
                    
                    await updateDoc(mentorRef, {
                      reminderSettings: updatedSettings
                    });
                    
                    // Update local state
                    setNextNotificationDateAndTime(utcDate.toISOString());
                    setReminderSettings(updatedSettings);
                    
                    // Update display
                    const dateInTz = toZonedTime(utcDate, userTimezone);
                    const dateStr = format(dateInTz, 'MM/dd/yyyy');
                    const timeStr = format(dateInTz, 'h:mm a');
                    setNextNotificationDateWithTime(`${dateStr} at ${timeStr}`);
                    
                    setShowNextNotificationDatePicker(false);
                  } catch (error) {
                    console.error('Error updating next notification date:', error);
                    setError('Failed to update next notification date');
                  } finally {
                    setIsSaving(false);
                  }
                }}
                className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:bg-green-300"
                disabled={isSaving || !nextNotificationDateInput || !nextNotificationTimeInput}
              >
                {isSaving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DiscipleSettings;